import { Button } from "@chakra-ui/react"
import { useColorModeValue } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import withTranslate, {
  useTranslate
} from "@pathwright/ux/src/components/lng/withTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { SchoolGroupSubscriptionFragment } from "@pathwright/graphql-client/src/generated"
import {
  ENROLLMENT_ROLE_STUDENT,
  ROLE_LABELS,
  ROLE_TYPE_OFFERING
} from "@pathwright/features/src/modules/invitation/constants"
import { useOrderFulfillment } from "@pathwright/features/src/modules/order/OrderStripeCheckoutForm"
import { withUserCommunity } from "@pathwright/features/src/modules/user/UserCommunityContext"
import ConnectStores from "lib/core/store/components/ConnectStores"
import assign from "lodash/assign"
import {
  REGISTER_STATE_PAYMENT,
  REGISTER_STATE_REGISTERABLE,
  REGISTER_STATE_REGISTERED,
  REGISTER_STATE_SUBSCRIBE,
  REGISTER_STATE_UNUSABLE_SUBSCRIPTION
} from "../constants"
import OrderRegistrationButton from "./OrderRegistrationButton"
import Register from "./Register"

const tPrefix = "auth.signed_in_footer"

type ContinueAction = {
  onContinue: () => void
  continuePrompt: string
  nextRegisterState: number | null
}

type NextRegisterStateButtonProps = {
  continueAction: ContinueAction
  getContinueAction: () => ContinueAction
  is_registering: boolean
  registration_loading: boolean
  attemptToConvert?: boolean
  offerConversionActionLabel?: string
}

const NextRegisterStateButton = function ({
  continueAction,
  is_registering,
  registration_loading,
  offerConversionActionLabel,
  attemptToConvert = false
}: NextRegisterStateButtonProps) {
  const { t } = useTranslate()
  const colorPalette = useColorModeValue("brand", undefined)
  const { onContinue, continuePrompt, nextRegisterState } = continueAction
  const handleOrderFulfillment = useOrderFulfillment()

  return continuePrompt && !registration_loading ? (
    <OrderRegistrationButton
      submitLabel={continuePrompt}
      onOrderFulfilled={handleOrderFulfillment}
    >
      {nextRegisterState === REGISTER_STATE_PAYMENT ? (
        <Register
          // @ts-expect-error: not inferring typing.
          attemptToConvert={attemptToConvert}
          offerConversionActionLabel={offerConversionActionLabel}
        />
      ) : nextRegisterState === REGISTER_STATE_REGISTERABLE ? null : (
        <Button
          colorPalette={colorPalette}
          variant="solid"
          onClick={() => onContinue()}
          loading={is_registering}
          disabled={is_registering}
          width="full"
        >
          {continuePrompt}
          {continuePrompt === t(`${tPrefix}.continue`) && (
            <Pathicon icon="arrow-right" />
          )}
        </Button>
      )}
    </OrderRegistrationButton>
  ) : null
}

NextRegisterStateButton.displayName = "NextRegisterStateButton"

const mapStateToProps = function (state: any, ownProps: any) {
  const { t, offerConversionActionLabel } = ownProps

  const {
    offering,
    invitation,
    resource,
    is_registering,
    registration_loading
  } = state.registerStore.state
  const { setRegisterState } = state.registerStore.action
  const { accessURL, getNextRegisterState } = state.registerStore.request

  const useGroupSubscriptionIds = (
    ownProps.communityGroupSubscriptions as SchoolGroupSubscriptionFragment[]
  )
    .filter((cgs) =>
      offering?.include_with_products.includes(cgs.price.product?.id)
    )
    .map((cgs) => cgs.id)

  const getContinueAction = () => {
    let continuePrompt
    let onContinue
    // Subscription fulfillment can render this action while the store reloads.
    const nextRegisterState = offering
      ? getNextRegisterState({
          includedInGroupSubscription: useGroupSubscriptionIds
        })
      : null

    switch (nextRegisterState) {
      case REGISTER_STATE_REGISTERED:
        // "Continue Learning" - not always appropriate. Need to scope to user role.
        continuePrompt = t("resource.offering_form.continue")
        onContinue = () => {
          // Navigate to the path since already registered
          window.App.navigate(accessURL(resource, offering.id))
        }
        break
      case REGISTER_STATE_PAYMENT:
      case REGISTER_STATE_UNUSABLE_SUBSCRIPTION:
        continuePrompt = t(`${tPrefix}.continue`)
        onContinue = setRegisterState
        break
      case REGISTER_STATE_SUBSCRIBE:
        continuePrompt = t("resource.offering_form.subscribe")
        onContinue = setRegisterState
        break
      case REGISTER_STATE_REGISTERABLE:
        continuePrompt = offerConversionActionLabel || t(`${tPrefix}.register`)
        // OrderRegistrationButton submits the $0 registration order.
        onContinue = () => {}
        if (
          invitation != null &&
          invitation.role &&
          invitation.role !== ENROLLMENT_ROLE_STUDENT
        ) {
          const roles = ROLE_LABELS[ROLE_TYPE_OFFERING]
          const roleLabel = roles[invitation.role as keyof typeof roles]

          const role = t(
            `${tPrefix}.offering_role_labels.${roleLabel.split(" ").join("_")}`
          )
          continuePrompt = `${continuePrompt} ${t(
            `${tPrefix}.as`
          )} ${role.toLowerCase()}`
        }
        break
    }

    return {
      continuePrompt,
      onContinue,
      nextRegisterState
    }
  }

  const continueAction = getContinueAction()

  return assign(
    {},
    {
      getContinueAction,
      continueAction,
      is_registering,
      registration_loading,
      getNextRegisterState
    },
    ownProps
  )
}

const stores = [
  {
    store: "register",
    watch: ["offering", "is_registering", "registration_loading", "registration"]
  },
  { store: "resource", watch: false },
  { store: "subscribe", watch: ["subscription", "subscribe_state"] }
]

export default withUserCommunity(
  withTranslate(
    ConnectStores(NextRegisterStateButton, stores, {}, mapStateToProps)
  )
)
